import type { PropsWithChildren } from 'react'

import { Icon } from '@fv/client-components'
import type { PropsWithClassName, TrackEvent } from '@fv/client-types'
import type { InterlineDetails } from '@fv/models'

import { ListItem } from '@/components/List'
import { Pinline } from '@/components/Pinline'

import { formatTrackingDateTime } from './utils'

type ItemModel = TrackEvent

type Props = {
  item: ItemModel
}
export const TrackEventListItem = (props: Props) => {
  const hasException = !!props.item.exceptionDescription
  const location = formatEventLocation(props.item)
  return (
    <BaseTrackListItem className="relative -left-2 gap-x-2">
      <TrackingTimeStamp item={props.item} />

      <p className="mb-0">
        {hasException && (
          <Icon
            icon="exclamation-triangle"
            className="text-fv-orange-bright mr-2"
          />
        )}
        {hasException ? props.item.exceptionDescription : props.item.summary}
      </p>
      {!!location && (
        <>
          <Pinline className="flex-1 min-w-8 mt-[.65rem]" dotted />
          <div className="flex-none whitespace-nowrap text-sm fa-width-auto">
            <Icon icon="map-marker" className="text-fv-gray-dark" />
            <span>{location}</span>
          </div>
        </>
      )}
    </BaseTrackListItem>
  )
}

const formatEventLocation = (item: ItemModel) => {
  const cityState = [item.city, item.state].filter(Boolean).join(', ')
  const base = [cityState || undefined, item.postalCode]
    .filter(Boolean)
    .join(' ')
  const country =
    item.country && item.country.toLowerCase() !== 'us'
      ? item.country.toLocaleUpperCase()
      : undefined

  if (!base) return country ?? ''
  return country ? `${base}, ${country}` : base
}

type StopsAwayProps = {
  stopsAway: number
}
export const StopsAwayListItem = (props: StopsAwayProps) => {
  return (
    <BaseTrackListItem className="!items-center gap-x-2 relative -left-3">
      <div className="rounded-full h-6 w-6 flex items-center justify-center bg-fv-green-50 border-fv-green-300 border-2">
        {props.stopsAway}
      </div>
      <span>stops away from being delivered</span>
    </BaseTrackListItem>
  )
}

type InterlineItemProps = {
  interlineDetails: InterlineDetails
}
export const InterlineListItem = (props: InterlineItemProps) => {
  const { carrierName, carrierCode } = props.interlineDetails
  const carrierDisplay =
    carrierName && carrierCode ? (
      <>
        {carrierName} ({carrierCode})
      </>
    ) : (
      <span className="font-mono beer-container p-0.5">{carrierCode}</span>
    )
  return (
    <BaseTrackListItem className="!items-center gap-x-2 relative -left-3">
      <div className="rounded-full h-6 w-6 flex items-center justify-center bg-fv-beer-xlight border-fv-beer-medium border-2">
        <Icon
          icon="truck-fast"
          transform="shrink-6"
          className="text-fv-gray-dark"
        />
      </div>
      <span>
        Interline carrier assigned. {carrierDisplay} / PRO#{' '}
        {props.interlineDetails.pro}
      </span>
    </BaseTrackListItem>
  )
}

const BaseTrackListItem = (props: PropsWithChildren<PropsWithClassName>) => {
  return (
    <ListItem itemsStart dashed {...props}>
      {props.children}
    </ListItem>
  )
}

type TimestampProps = {
  item: TrackEvent
}
const TrackingTimeStamp = ({ item }: TimestampProps) => {
  return (
    <div className="text-sm italic bg-white px-2 rounded-xs border border-fv-gray-500 relative w-20 text-right whitespace-nowrap flex-none">
      {item.eventDateTime.time
        ? formatTrackingDateTime(item.eventDateTime, {
            timeFormat: 'h:mm A',
            dateFormat: '',
          })
        : 'Unknown'}
    </div>
  )
}
